import { AlertTriangle, Play, Upload } from 'lucide-react'
import { toast } from 'sonner'

import {
  useCronHealth,
  useGlobalEnabled,
  useRunGenerationAll,
  useRunUploadAll,
  useSetGlobalEnabled,
  type CycleConfigOut,
  type SchedulerStateOut,
} from '@/api/endpoints/scheduler'
import { Button } from '@/components/ui/Button'
import { Toggle } from '@/components/ui/Toggle'
import { formatWhen } from '@/features/scheduler/channelCards'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import { cronAlerts } from './cronAlerts'
import styles from './ScheduleControls.module.scss'

const RUN_STATUS: Readonly<Record<string, string>> = {
  completed: 'concluída',
  partial: 'parcial',
  failed: 'falhou',
  running: 'rodando',
}

function lastRun(cycle: { last_run_date: string; last_run_status: string }, now: Date): string {
  if (!cycle.last_run_date) return 'nunca rodou'
  const status = RUN_STATUS[cycle.last_run_status] ?? cycle.last_run_status
  return status ? `${formatWhen(cycle.last_run_date, now)} · ${status}` : formatWhen(cycle.last_run_date, now)
}

interface ScheduleControlsProps {
  generation: CycleConfigOut
  upload: CycleConfigOut
  state: SchedulerStateOut
  now: Date
}

/**
 * A barra do cabeçalho do Scheduler: o interruptor que pausa todos os ciclos e
 * os dois disparos "em todos os canais". Pausar zera ``generation.enabled`` e
 * ``upload.enabled`` juntos; o estado por canal fica como está e volta ao
 * religar. Mora aqui porque é no Scheduler que se pausa (incidente, deploy, API
 * fora do ar).
 */
export function ScheduleControls({ generation, upload, state, now }: ScheduleControlsProps) {
  const global = useGlobalEnabled()
  const setGlobal = useSetGlobalEnabled()
  const runGen = useRunGenerationAll()
  const runUpl = useRunUploadAll()
  const enabled = global.data?.enabled ?? true
  const alerts = cronAlerts(useCronHealth().data ?? [], now)
  const [firstAlert] = alerts

  function handleChange(next: boolean) {
    setGlobal.mutate(next, {
      onSuccess: () => {
        toast.success(next ? 'Schedules ativados' : 'Todos os schedules pausados')
      },
      onError: (err) => {
        toast.error(errorMessage(err, 'Falha ao alternar'))
      },
    })
  }

  return (
    <div className={styles.controls}>
      <div className={styles.bar}>
        <label className={cn(styles.label, !enabled && styles.paused)} htmlFor="global-schedule-toggle">
          {enabled ? 'Schedules ativos' : 'Schedules pausados'}
        </label>
        <Toggle
          id="global-schedule-toggle"
          checked={enabled}
          disabled={global.isLoading || setGlobal.isPending}
          onCheckedChange={handleChange}
          title="Pausa todos os ciclos (geração e upload). O estado de cada canal volta ao religar."
        />
        <span className={styles.divider} aria-hidden />
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Play />}
          disabled={runGen.isPending || !generation.enabled}
          title="Gera agora em todos os canais ativos"
          onClick={() => {
            runGen.mutate(undefined, {
              onSuccess: () => toast.success('Geração disparada em todos os canais ativos'),
              onError: (err) => toast.error(errorMessage(err, 'Não foi possível disparar a geração')),
            })
          }}
        >
          {runGen.isPending ? 'Disparando…' : 'Gerar todos'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Upload />}
          disabled={runUpl.isPending || !upload.enabled}
          title="Roda o upload agora em todos os canais ativos"
          onClick={() => {
            runUpl.mutate(undefined, {
              onSuccess: () => toast.success('Upload disparado em todos os canais ativos'),
              onError: (err) => toast.error(errorMessage(err, 'Não foi possível disparar o upload')),
            })
          }}
        >
          {runUpl.isPending ? 'Disparando…' : 'Publicar todos'}
        </Button>
      </div>
      <p className={styles.caption}>
        Geração {lastRun(state.generation, now)} <span aria-hidden>·</span> Upload {lastRun(state.upload, now)}
      </p>
      {firstAlert ? (
        <p
          className={styles.alert}
          role="status"
          title={alerts.map((a) => `${a.label}: ${a.detail}`).join('\n')}
        >
          <AlertTriangle aria-hidden />
          <span>
            {alerts.length === 1
              ? `${firstAlert.label}: ${firstAlert.detail}`
              : `${String(alerts.length)} rotinas com problema · ${firstAlert.label}: ${firstAlert.detail}`}
          </span>
        </p>
      ) : null}
    </div>
  )
}
